import type { Game } from '@/types/game';

/**
 * Per-game feature detection for the Hub.
 *
 * The Hub renders game-specific sections only when the active game supports
 * them. Game ids are backend-defined (Laravel `games` table) and can drift
 * between staging and prod, so we match on the game NAME first (stable,
 * human-authored) and fall back to a known id.
 *
 * `CR_GAME_ID = 6` is the staging+prod Clash Royale id, previously hardcoded
 * in `src/routes/Tournaments.tsx`. Kept as a fallback for the rare case where
 * the name doesn't match the regex (renamed game variant, locale label, …).
 */
export const CR_GAME_ID = 6;

/**
 * Static Clash Royale fallback. Unlike the synthetic games below, CR IS a
 * real Laravel `games` row (id 6) — but a fresh user can reach the Hub
 * before `GET /games/available` resolves (or while the Laravel pipeline is
 * down, e.g. the unverified-email limbo). The public launch is CR-exclusive,
 * so `useGameSelection` injects this fallback whenever the backend list
 * doesn't (yet) contain id 6; the real row wins once it arrives.
 */
export const CR_GAME: Game = {
  id: CR_GAME_ID,
  name: 'Clash Royale',
  is_global: true,
  label: 'Clash Royale',
  required_template: 0,
  display_in_tournaments: true,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '',
  image: null,
  icon: '',
  top_banner: '',
  web_icon: '',
  platforms: [],
};

/**
 * Synthetic client-side game for QChess. Chess is NOT a Laravel `games` row
 * (it's a separate microservice), so `useGameSelection` injects this
 * pseudo-game into the picker when the chess feature flag is on — making it
 * selectable like Clash Royale / eFootball. The id is a large positive
 * sentinel that won't collide with backend ids and stays > 0 so the
 * localStorage rehydrate in `useGameSelection` accepts it. Selecting it lights
 * up the Hub's chess section via `featuresForGame()`.
 */
export const CHESS_GAME_ID = 999001;

export const CHESS_GAME: Game = {
  id: CHESS_GAME_ID,
  name: 'Chess',
  is_global: true,
  label: 'Chess',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/chess.svg',
  image: '/games/chess.svg',
  icon: '/games/chess.svg',
  top_banner: '/games/chess.svg',
  web_icon: '/games/chess.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QClans (Clash of Clans). Like QChess, CoC is
 * NOT a Laravel `games` row (separate microservice), so `useGameSelection`
 * injects this pseudo-game into the picker when the CoC feature flag is on —
 * making it selectable like Clash Royale / eFootball / Chess. Large positive
 * sentinel id that won't collide with backend ids and stays > 0 so the
 * localStorage rehydrate accepts it. Selecting it lights up the Hub's CoC
 * section via `featuresForGame()`.
 */
export const COC_GAME_ID = 999002;

export const COC_GAME: Game = {
  id: COC_GAME_ID,
  name: 'Clash of Clans',
  is_global: true,
  label: 'Clash of Clans',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/clash-of-clans.svg',
  image: '/games/clash-of-clans.svg',
  icon: '/games/clash-of-clans.svg',
  top_banner: '/games/clash-of-clans.svg',
  web_icon: '/games/clash-of-clans.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QGammon (Backgammon). Like QChess / QClans,
 * backgammon is NOT a Laravel `games` row (it's a separate first-party
 * microservice), so `useGameSelection` injects this pseudo-game into the picker
 * when the backgammon feature flag is on — making it selectable like the
 * others. Large positive sentinel id (won't collide with backend ids, stays > 0
 * so the localStorage rehydrate accepts it). Selecting it lights up the Hub's
 * backgammon section via `featuresForGame()`.
 */
export const BACKGAMMON_GAME_ID = 999003;

export const BACKGAMMON_GAME: Game = {
  id: BACKGAMMON_GAME_ID,
  name: 'Backgammon',
  is_global: true,
  label: 'Backgammon',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/backgammon.svg',
  image: '/games/backgammon.svg',
  icon: '/games/backgammon.svg',
  top_banner: '/games/backgammon.svg',
  web_icon: '/games/backgammon.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QChinesePoker (Pineapple OFC + Fantasyland).
 * Like QChess / QClans / QGammon, QChinesePoker is NOT a Laravel `games` row
 * (separate first-party microservice), so `useGameSelection` injects this
 * pseudo-game into the picker when the Chinese Poker feature flag is on —
 * making it selectable like the others. Large positive sentinel id (won't
 * collide with backend ids, stays > 0 so the localStorage rehydrate accepts
 * it). Selecting it lights up the Hub's Chinese Poker section via
 * `featuresForGame()`.
 */
export const CHINESE_POKER_GAME_ID = 999004;

export const CHINESE_POKER_GAME: Game = {
  id: CHINESE_POKER_GAME_ID,
  name: 'Chinese Poker',
  is_global: true,
  label: 'Chinese Poker',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/chinese-poker.svg',
  image: '/games/chinese-poker.svg',
  icon: '/games/chinese-poker.svg',
  top_banner: '/games/chinese-poker.svg',
  web_icon: '/games/chinese-poker.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QBridge (I-609, Luca 11/10: «quando cerco di cambiare gioco in tma staging vedo solo clash royale,
 * chess e backgammon»). Like the others, bridge is NOT a Laravel `games` row (it is a first-party game host), so
 * `useGameSelection` injects this pseudo-game into the picker — but only on a build that has the bridge (the LITERAL flag
 * comparison, which the bundler folds away in production) and only for the role that can enter it. Large positive sentinel id
 * (won't collide with backend ids, stays > 0 so the localStorage rehydrate accepts it). Selecting it lights up the Hub's bridge
 * hub via `featuresForGame()`. The name is a BRAND, the same in every language (L-2645), so there is no catalogue entry.
 */
export const BRIDGE_GAME_ID = 999005;

export const BRIDGE_GAME: Game = {
  id: BRIDGE_GAME_ID,
  name: 'Bridge',
  is_global: true,
  label: 'Bridge',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [4],
  reg_ex: null,
  web_image: '/games/bridge.svg',
  image: '/games/bridge.svg',
  icon: '/games/bridge.svg',
  top_banner: '/games/bridge.svg',
  web_icon: '/games/bridge.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for QBurraco (ingresso al Burraco nella TMA, PR1 di qburraco-orcb-39; condizioni Z1-Z13 di tma-orcb, 12/10; schema del Bridge,
 * I-609). Like the others, burraco is NOT a Laravel `games` row (it is a first-party game host), so `useGameSelection` injects this pseudo-game into the
 * picker — only on a build that has the burraco AND Limulo (the LITERAL flag comparison, which the bundler folds away in production) and only for the role
 * that can enter it (`beta`). Large positive sentinel id (won't collide with backend ids nor with the other synthetic ones: 999001-999005; stays > 0 so the
 * localStorage rehydrate accepts it). Selecting it lights up the Hub's burraco hub via `featuresForGame()`. The name is a BRAND, the same in every language,
 * so there is no catalogue entry. Its tournaments are the ones of the Limulo catalogue with `profileType: 'burraco'` (`PROFILE_TYPE_DEL_GIOCO`).
 */
export const BURRACO_GAME_ID = 999006;

export const BURRACO_GAME: Game = {
  id: BURRACO_GAME_ID,
  name: 'Burraco',
  is_global: true,
  label: 'Burraco',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 4],
  reg_ex: null,
  web_image: '/games/burraco.svg',
  image: '/games/burraco.svg',
  icon: '/games/burraco.svg',
  top_banner: '/games/burraco.svg',
  web_icon: '/games/burraco.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the SCOPA (kit-giochi S4, ADR-0235: scopa and scopone on the table-game kit, game host QScopa). Like the burraco it is NOT a
 * Laravel `games` row, so `useGameSelection` injects it — only on a build that has the scopa AND Limulo (the LITERAL flag comparison, which the bundler folds away
 * in production) and only for `beta`. Sentinel id 999007 (after 999001-999006). Selecting it lights up the scopa hub via `featuresForGame()`. The name is the
 * game's own name, the same in every language. The scopa tournaments are in `HubScopa` (`TorneiScopa`, `profileType` 'scopa' in `giocoSelezionato.ts`); the «Ranked» tab (the queue)
 * and the leaderboard stay out.
 */
export const SCOPA_GAME_ID = 999007;

export const SCOPA_GAME: Game = {
  id: SCOPA_GAME_ID,
  name: 'Scopa',
  is_global: true,
  label: 'Scopa',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 4],
  reg_ex: null,
  web_image: '/games/scopa.svg',
  image: '/games/scopa.svg',
  icon: '/games/scopa.svg',
  top_banner: '/games/scopa.svg',
  web_icon: '/games/scopa.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the BRISCOLA (kit-giochi P6, ADR-0235: briscola 1v1 and 2v2 on the table-game kit, game host QBriscola). Like the scopa it is NOT a Laravel
 * `games` row, so `useGameSelection` injects it — only on a build that has the briscola AND Limulo (the LITERAL flag comparison, which the bundler folds away in production)
 * and only for `beta`. Sentinel id 999009, assigned by tma-orc (2026-10-20 19:54:49Z; 999010 gin rummy, 999011 scala 40, 999012 2048 follow). Selecting it lights up the
 * briscola hub via `featuresForGame()`. The name is the game's own name, the same in every language. No tournaments here: ranked and tournaments are P9.
 */
export const BRISCOLA_GAME_ID = 999009;

export const BRISCOLA_GAME: Game = {
  id: BRISCOLA_GAME_ID,
  name: 'Briscola',
  is_global: true,
  label: 'Briscola',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 4],
  reg_ex: null,
  web_image: '/games/briscola.svg',
  image: '/games/briscola.svg',
  icon: '/games/briscola.svg',
  top_banner: '/games/briscola.svg',
  web_icon: '/games/briscola.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the SOLITARIO (Klondike a torneo; QSolitario, first-party game host; TMA PR 1 of 3). Like the burraco it is NOT a Laravel `games`
 * row, so `useGameSelection` injects it — only on a build that has the solitario AND Limulo (the LITERAL flag comparison, which the bundler folds away in
 * production) and only for `beta`. Sentinel id 999008 (after 999001-999006 and 999007, which the scopa branch #735 takes; stays > 0 so the localStorage rehydrate
 * accepts it). Selecting it lights up the solitario hub via `featuresForGame()`. The name is the SECTION's, «Solitari», the SAME in every language (contratto «Solitari» T1; the other synthetic entries have one name too). No Limulo
 * tournaments yet (qTournaments comes later: `PROFILE_TYPE_DEL_GIOCO` says `null`).
 * ⚠️ `participant_number` e' [1, 2, 4]: 1 = la pratica e la smazzata del giorno (una persona), 2 e 4 = i posti del tavolo, asincrono o dal vivo
 * (`docs/protocollo.md` di QSolitario, `posti: 2 | 4`). Oggi nessun codice della TMA legge il campo.
 */
export const SOLITARIO_GAME_ID = 999008;

export const SOLITARIO_GAME: Game = {
  id: SOLITARIO_GAME_ID,
  name: 'Solitari',
  is_global: true,
  label: 'Solitari',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [1, 2, 4],
  reg_ex: null,
  web_image: '/games/solitario.svg',
  image: '/games/solitario.svg',
  icon: '/games/solitario.svg',
  top_banner: '/games/solitario.svg',
  web_icon: '/games/solitario.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the GIN RUMMY (P6 of the gin rummy contract, ADR-0235: the first game of the kit with the French 52-card deck; game host QGinRummy). Like the scopa it is
 * NOT a Laravel `games` row, so `useGameSelection` injects it — only on a build that has the gin rummy AND Limulo (the LITERAL flag comparison, which the bundler folds away in
 * production) and only for `beta`. Sentinel id 999010 (the one the kit reserved for it: «usate il vostro e solo quello», tma-orc 2026-10-20). Selecting it lights up the gin rummy hub
 * via `featuresForGame()`. The name is the game's own name, «Gin Rummy», the same in every language (Luca, I-837). No tournaments here: ranked and tournaments come later.
 */
export const GINRUMMY_GAME_ID = 999010;

export const GINRUMMY_GAME: Game = {
  id: GINRUMMY_GAME_ID,
  name: 'Gin Rummy',
  is_global: true,
  label: 'Gin Rummy',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/ginrummy.svg',
  image: '/games/ginrummy.svg',
  icon: '/games/ginrummy.svg',
  top_banner: '/games/ginrummy.svg',
  web_icon: '/games/ginrummy.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the SCALA 40 (P6 of the scala 40 contract, ADR-0235: the first game of the kit with more than two seats at a free table and the 108-card deck with jokers; game host QScala40).
 * Like the gin rummy it is NOT a Laravel `games` row, so `useGameSelection` injects it — only on a build that has the scala 40 AND Limulo (the LITERAL flag comparison, which the bundler folds away in
 * production) and only for `beta`. Sentinel id 999011, assigned by tma-orc (2026-10-20 19:54:49Z, in the comment of `BRISCOLA_GAME_ID`: 999010 gin rummy, 999011 scala 40). Selecting it lights up the scala 40
 * hub via `featuresForGame()`. The name is the game's own name, «Scala 40», the same in every language. No tournaments here: ranked and tournaments are P9.
 */
export const SCALA40_GAME_ID = 999011;

export const SCALA40_GAME: Game = {
  id: SCALA40_GAME_ID,
  name: 'Scala 40',
  is_global: true,
  label: 'Scala 40',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 3, 4, 5, 6],
  reg_ex: null,
  web_image: '/games/scala40.svg',
  image: '/games/scala40.svg',
  icon: '/games/scala40.svg',
  top_banner: '/games/scala40.svg',
  web_icon: '/games/scala40.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the SETTE E MEZZO (P6 of the sette e mezzo contract, ADR-0235: one on one, the bank changes every hand, 40-card Neapolitan deck; game host QSetteMezzo). Like the gin rummy it is
 * NOT a Laravel `games` row, so `useGameSelection` injects it — only on a build that has the sette e mezzo AND Limulo (the LITERAL flag comparison, which the bundler folds away in production) and only for
 * `beta`. Sentinel id 999013, assigned by tma-orc (2026-10-21 14:00Z). Selecting it lights up the sette e mezzo hub via `featuresForGame()`. The name is the game's own name,
 * «Sette e mezzo», the same in every language. No tournaments here: ranked and tournaments do not enter (the game is born friendly, against the robot and by invitation).
 */
export const SETTEMEZZO_GAME_ID = 999013;

export const SETTEMEZZO_GAME: Game = {
  id: SETTEMEZZO_GAME_ID,
  name: 'Sette e mezzo',
  is_global: true,
  label: 'Sette e mezzo',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2],
  reg_ex: null,
  web_image: '/games/settemezzo.svg',
  image: '/games/settemezzo.svg',
  icon: '/games/settemezzo.svg',
  top_banner: '/games/settemezzo.svg',
  web_icon: '/games/settemezzo.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the TRESSETTE (P6 of the tressette contract, ADR-0235: one on one with the stock, two on two in pairs, 40-card Neapolitan deck; game host QTressette). Like the settemezzo it is
 * NOT a Laravel `games` row, so `useGameSelection` injects it — only on a build that has the tressette AND Limulo (the LITERAL flag comparison, which the bundler folds away in production) and only for
 * `beta`. Sentinel id 999014, assigned by tma-orc (2026-10-23 08:35Z; 999013 is the sette e mezzo). Selecting it lights up the tressette hub via `featuresForGame()`. The name is the game's own name,
 * «Tressette», the same in every language. No tournaments here: ranked, tournaments and queues are face B, out of this PR.
 */
export const TRESSETTE_GAME_ID = 999014;

export const TRESSETTE_GAME: Game = {
  id: TRESSETTE_GAME_ID,
  name: 'Tressette',
  is_global: true,
  label: 'Tressette',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 4],
  reg_ex: null,
  web_image: '/games/tressette.svg',
  image: '/games/tressette.svg',
  icon: '/games/tressette.svg',
  top_banner: '/games/tressette.svg',
  web_icon: '/games/tressette.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the PILOTTA (P8 of the pilotta contract, ADR-0235: the Cypriot trick-taking game, four seats, two on two in pairs, friendly games only, 32-card French deck; game host QPilotta).
 * Like the tressette it is NOT a Laravel `games` row, so `useGameSelection` injects it — only on a build that has the pilotta AND Limulo (the LITERAL flag comparison, which the bundler folds away in production)
 * and only for `beta`. Sentinel id 999015, assigned by tma-orcr (2026-10-31 03:5xZ; 999012 is reserved for the 2048). Selecting it lights up the pilotta hub via `featuresForGame()`. The name is the game's own name, «Pilotta», the same in every language. No tournaments, no ranked, no queues: the contract has none.
 */
export const PILOTTA_GAME_ID = 999015;

export const PILOTTA_GAME: Game = {
  id: PILOTTA_GAME_ID,
  name: 'Pilotta',
  is_global: true,
  label: 'Pilotta',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [4],
  reg_ex: null,
  web_image: '/games/pilotta.svg',
  image: '/games/pilotta.svg',
  icon: '/games/pilotta.svg',
  top_banner: '/games/pilotta.svg',
  web_icon: '/games/pilotta.svg',
  platforms: [],
};

/**
 * Synthetic client-side game for the MPIRIMPA / BIRIBA (Μπιρίμπα; ADR-0265: the lock-step burraco engine, two or four seats, friendly games; game host QMpirimpa). Like the pilotta it is NOT a Laravel `games` row,
 * so `useGameSelection` injects it — only on a build that has the mpirimpa AND Limulo (the LITERAL flag comparison, which the bundler folds away in production) and only for `beta`. Sentinel id 999016, assigned by
 * tma-orcr (2026-10-31 12:0xZ; 999001-999015 are taken, 999012 is reserved for the 2048). Selecting it lights up the mpirimpa hub via `featuresForGame()`. The pickers (`GamePicker`, `GameDropdown`, `RigaRisorse`) render `Game.name`, NOT `Game.label` (measured by tma-ui-orcb on a9a61211), and `name` is ONE string
 * (no per-language mechanism), so `name` is «Mpirimpa (Biriba)» (`label` is kept equal); the per-language name («Μπιρίμπα» in Greek) lives inside the section, from `locales/<lng>/mpirimpa.json`.
 * The synthetic game is recognised BY ID in `featuresForGame()`; `MPIRIMPA_NAME_RE` serves the names of the Limulo catalogue.
 */
export const MPIRIMPA_GAME_ID = 999016;

export const MPIRIMPA_GAME: Game = {
  id: MPIRIMPA_GAME_ID,
  name: 'Mpirimpa (Biriba)',
  is_global: true,
  label: 'Mpirimpa (Biriba)',
  required_template: 0,
  display_in_tournaments: false,
  ios_label: null,
  participant_number: [2, 4],
  reg_ex: null,
  web_image: '/games/mpirimpa.svg',
  image: '/games/mpirimpa.svg',
  icon: '/games/mpirimpa.svg',
  top_banner: '/games/mpirimpa.svg',
  web_icon: '/games/mpirimpa.svg',
  platforms: [],
};

/**
 * Riconoscitori per NOME, ed e' l'unica chiave che attraversa i sistemi: gli `id` dei
 * giochi non sono gli stessi in Laravel e in Limulo (`services/limuloApi.ts` lo dice per
 * `GET /v1/games`: «`id` qui e' un ObjectId, non l'id numerico del monolite»).
 * ⚠️ Esportati perche' `lib/limulo/profiles.ts` deve riconoscere lo stesso gioco fra le
 * righe di Limulo: una seconda copia della regex qui accanto divergerebbe in silenzio.
 * L'ordine CoC-prima-di-CR e' vincolante e vale per ogni chiamante (v. `featuresForGame`).
 */
export const CR_NAME_RE = /clash\s*royale/i;
const RIOT_NAME_RE =
  /league of legends|teamfight|\btft\b|legends of runeterra|valorant/i;
const CHESS_NAME_RE = /chess|scacchi/i;
// "Clash of Clans" only — must NOT match "Clash Royale" (handled by CR_NAME_RE).
export const COC_NAME_RE = /clash\s*of\s*clans/i;
const BACKGAMMON_NAME_RE = /backgammon|tavola\s*reale/i;
const CHINESE_POKER_NAME_RE = /chinese\s*poker|pineapple\s*ofc|ofc\s*poker/i;
// Il nome INTERO e solo quello: «bridge» dentro un altro nome non e' il bridge.
const BRIDGE_NAME_RE = /^\s*bridge\s*$/i;
// Idem: il nome INTERO e solo quello.
const BURRACO_NAME_RE = /^\s*burraco\s*$/i;
// Idem: il nome INTERO e solo quello.
const BRISCOLA_NAME_RE = /^\s*briscola\s*$/i;
// Idem: «scopa» o «scopone», il nome intero.
const SCOPA_NAME_RE = /^\s*scop(?:a|one)\s*$/i;
// Idem: il nome INTERO e solo quello.
const SOLITARIO_NAME_RE = /^\s*solitario\s*$/i;
// Idem: «gin rummy», il nome intero (anche «ginrummy», come il dominio).
const GINRUMMY_NAME_RE = /^\s*gin\s*rummy\s*$/i;
// Idem: «scala 40» (anche «scala40», come il dominio), il nome intero.
const SCALA40_NAME_RE = /^\s*scala\s*40\s*$/i;
// Idem: «sette e mezzo» (anche «settemezzo», come il dominio), il nome intero.
const SETTEMEZZO_NAME_RE = /^\s*sette\s*e?\s*mezzo\s*$/i;
// Idem: «tressette», il nome intero.
const TRESSETTE_NAME_RE = /^\s*tressette\s*$/i;
// Idem: «pilotta», il nome intero.
const PILOTTA_NAME_RE = /^\s*pilotta\s*$/i;
// Idem: «mpirimpa», «mpirimpa (biriba)» (il nome del selettore), «biriba» o «μπιρίμπα» (il nome greco, con e SENZA tonos: «ΜΠΙΡΙΜΠΑ» in maiuscolo non lo porta), il nome intero.
// Serve ai nomi del catalogo Limulo: il gioco sintetico del selettore si riconosce per ID (`game.id === MPIRIMPA_GAME_ID`, in `featuresForGame`).
const MPIRIMPA_NAME_RE = /^\s*(?:mpirimpa|mpirimpa\s*\(\s*biriba\s*\)|biriba|μπιρίμπα|μπιριμπα)\s*$/i;

export interface GameFeatures {
  /** Clash Royale section (decks + battles + AI coach). */
  cr: boolean;
  /** Riot Games section (LoL / TFT / LoR / Valorant). */
  riot: boolean;
  /** QChess section (Lichess tournaments / duels / leaderboard). */
  chess: boolean;
  /** QClans section (Clash of Clans stats / war / competitions / coach). */
  coc: boolean;
  /** QGammon section (real-time backgammon 1v1 + ELO leaderboard). */
  backgammon: boolean;
  /** QChinesePoker section (heads-up Pineapple OFC + Fantasyland). */
  chinesePoker: boolean;
  /** QBridge hub (I-609): the tessera «Prima mano e lezioni», and nothing else for now. */
  bridge: boolean;
  /** QBurraco hub (ingresso, PR1): «I miei tavoli / Riprendi» e i tornei del gioco. */
  burraco: boolean;
  /** Scopa hub (kit-giochi S4): Gioca, Invita un amico, Entra con un codice, I miei tavoli. */
  scopa: boolean;
  /** Briscola hub (kit-giochi P6): Gioca, contro i robot, Invita un amico, Entra con un codice. */
  briscola: boolean;
  /** Solitario hub (TMA PR 1: la sezione vuota). */
  solitario: boolean;
  /** Gin rummy hub (P6 del contratto del gin): Gioca, Invita un amico, Entra con un codice, I miei tavoli. */
  ginRummy: boolean;
  /** Scala 40 hub (P6 del contratto della scala 40): Gioca (da 2 a 6 posti), Invita un amico, Entra con un codice, I miei tavoli. */
  scala40: boolean;
  /** Sette e mezzo hub (P6 del contratto del sette e mezzo): Gioca, Contro il robot, Invita un amico, Entra con un codice, I miei tavoli. */
  settemezzo: boolean;
  /** Tressette hub (P6 del contratto del tressette): Gioca (1v1 o 2v2), Contro i robot, Invita un amico, Entra con un codice, I miei tavoli. */
  tressette: boolean;
  /** Pilotta hub (P8 del contratto della Pilotta): Gioca (traguardo 151/251/301), Contro i robot, Invita un amico, Entra con un codice, I miei tavoli. */
  pilotta: boolean;
  /** Mpirimpa (Biriba) hub (PR-A, lo scheletro): la tessera e la pagina `/mpirimpa`; il tavolo arriva con la PR-B. */
  mpirimpa: boolean;
}

/**
 * Resolve which Hub feature sections a given game unlocks. Name-based match is
 * primary; the CR / Chess / CoC / Backgammon ids are fallbacks. Returns
 * all-false for `null` (no active game selected).
 */
export function featuresForGame(game: Game | null | undefined): GameFeatures {
  if (!game)
    return { cr: false, riot: false, chess: false, coc: false, backgammon: false, chinesePoker: false, bridge: false, burraco: false, scopa: false, briscola: false, solitario: false, ginRummy: false, scala40: false, settemezzo: false, tressette: false, pilotta: false, mpirimpa: false };
  const name = game.name ?? '';
  // CoC first so "Clash of Clans" never falls through to the CR matcher.
  const coc = COC_NAME_RE.test(name) || game.id === COC_GAME_ID;
  const cr = !coc && (CR_NAME_RE.test(name) || game.id === CR_GAME_ID);
  const riot = RIOT_NAME_RE.test(name);
  const chess = CHESS_NAME_RE.test(name) || game.id === CHESS_GAME_ID;
  const backgammon =
    BACKGAMMON_NAME_RE.test(name) || game.id === BACKGAMMON_GAME_ID;
  const chinesePoker =
    CHINESE_POKER_NAME_RE.test(name) || game.id === CHINESE_POKER_GAME_ID;
  const bridge = BRIDGE_NAME_RE.test(name) || game.id === BRIDGE_GAME_ID;
  const burraco = BURRACO_NAME_RE.test(name) || game.id === BURRACO_GAME_ID;
  const scopa = SCOPA_NAME_RE.test(name) || game.id === SCOPA_GAME_ID;
  const briscola = BRISCOLA_NAME_RE.test(name) || game.id === BRISCOLA_GAME_ID;
  const solitario = SOLITARIO_NAME_RE.test(name) || game.id === SOLITARIO_GAME_ID;
  const ginRummy = GINRUMMY_NAME_RE.test(name) || game.id === GINRUMMY_GAME_ID;
  const scala40 = SCALA40_NAME_RE.test(name) || game.id === SCALA40_GAME_ID;
  const settemezzo = SETTEMEZZO_NAME_RE.test(name) || game.id === SETTEMEZZO_GAME_ID;
  const tressette = TRESSETTE_NAME_RE.test(name) || game.id === TRESSETTE_GAME_ID;
  const pilotta = PILOTTA_NAME_RE.test(name) || game.id === PILOTTA_GAME_ID;
  const mpirimpa = MPIRIMPA_NAME_RE.test(name) || game.id === MPIRIMPA_GAME_ID;
  return { cr, riot, chess, coc, backgammon, chinesePoker, bridge, burraco, scopa, briscola, solitario, ginRummy, scala40, settemezzo, tressette, pilotta, mpirimpa };
}
